import { useQuery } from '@tanstack/react-query';

import { getFacultiesAsHeadOfOptions } from '@/module/Faculty/model/FacultyQuery';
import { isTravelAdminRole, isTravelOfficeRole } from '@/module/Travel/travelRoles';

import { useFilter } from '@/core/components';

import { RoleType, useCurrentUser } from '@feature/security';
import { Id } from '@feature/utils';

interface TravelFacultyFilterProps {
  value: string | null;
  onChange: (value: string | null) => void;
  isDisabled: boolean;
  allowedIds?: Id[];
}

interface UseTravelFacultyFilterReturn {
  facultyId: Id | null;
  // null hides the filter: the backend fixes the scope for every remaining role, so there is nothing to choose.
  facultyFilterProps: TravelFacultyFilterProps | null;
}

// The backend scopes a dean's registry to every faculty they head, so restrictDean offers only those and empty means all of them.
export function useTravelFacultyFilter(restrictDean: boolean): UseTravelFacultyFilterReturn {
  const { currentRole } = useCurrentUser();
  const { id, ...filterProps } = useFilter();
  // For a vice-dean the backend answers the head-of lookup with the faculties where they are vice-dean.
  const facultyScoped = currentRole === RoleType.FACULTY_ADMIN || currentRole === RoleType.VICE_DEAN;
  const { data: headedFaculties } = useQuery({
    ...getFacultiesAsHeadOfOptions(),
    enabled: facultyScoped && restrictDean,
    // Without it a failed lookup leaves the dean an empty filter instead of an error.
    throwOnError: true,
  });

  if (isTravelAdminRole(currentRole) || isTravelOfficeRole(currentRole) || (facultyScoped && !restrictDean)) {
    return { facultyId: id, facultyFilterProps: { ...filterProps, isDisabled: false } };
  }
  if (facultyScoped) {
    if (headedFaculties == null) {
      return { facultyId: null, facultyFilterProps: { ...filterProps, isDisabled: true, allowedIds: [] } };
    }
    if (headedFaculties.length === 0) {
      return { facultyId: null, facultyFilterProps: null };
    }
    const allowedIds = headedFaculties.map(faculty => faculty.id);
    return {
      facultyId: allowedIds.some(allowed => String(allowed) === id) ? id : null,
      facultyFilterProps: { ...filterProps, isDisabled: false, allowedIds },
    };
  }

  return { facultyId: null, facultyFilterProps: null };
}
